import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  Group,
  Edit,
  Delete,
  Search,
  Add
} from '@mui/icons-material'
import axios from 'axios'

const RolesResponsabilidad = () => {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedRol, setSelectedRol] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    activo: true
  })

  useEffect(() => {
    loadRoles()
  }, [])

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/roles-responsabilidad', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setRoles(response.data)
    } catch (error) {
      setError('Error al cargar roles: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedRol(null)
    setFormData({
      nombre: '',
      descripcion: '',
      activo: true
    })
    setOpenDialog(true)
  }

  const handleEdit = (rol) => {
    setSelectedRol(rol)
    setFormData({
      nombre: rol.nombre,
      descripcion: rol.descripcion,
      activo: rol.activo
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      
      if (selectedRol) {
        // Editar
        await axios.put(`/api/roles-responsabilidad/${selectedRol.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Rol actualizado exitosamente')
      } else {
        // Crear
        await axios.post('/api/roles-responsabilidad', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Rol creado exitosamente')
      }
      
      setOpenDialog(false)
      loadRoles()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (rol) => {
    if (!window.confirm(`¿Está seguro de eliminar el rol "${rol.nombre}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/roles-responsabilidad/${rol.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Rol eliminado exitosamente')
      loadRoles()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const filteredRoles = roles.filter(rol =>
    rol.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (rol.descripcion && rol.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (loading) {
    return (
      <Box>
        <Typography>Cargando roles de responsabilidad...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Roles de Responsabilidad
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nuevo Rol
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar roles por nombre o descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRoles.map((rol) => (
              <TableRow key={rol.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {rol.nombre}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {rol.descripcion || 'Sin descripción'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={rol.activo ? 'Activo' : 'Inactivo'}
                    color={rol.activo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(rol)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(rol)}
                      title="Eliminar"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron roles de responsabilidad
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRol ? 'Editar Rol' : 'Nuevo Rol'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nombre del Rol"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción detallada del rol y sus responsabilidades"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                />
              }
              label="Rol activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.nombre}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RolesResponsabilidad